import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck, Wallet, GitFork, Snowflake, Activity, ArrowRight, Cpu, Lock,
  Zap, ScrollText, Users, Bot, TrendingUp, AlertTriangle, CheckCircle2, DollarSign,
  Play, Pause, Rocket, Globe, Terminal,
} from "lucide-react";

const NAV_LINKS = [
  { label: "Watch Demo", href: "#tour", testid: "nav-link-tour" },
  { label: "Architecture", href: "#architecture", testid: "nav-link-architecture" },
  { label: "Safety Plane", href: "#guardrails", testid: "nav-link-guardrails" },
  { label: "Live Sandbox", href: "#simulation", testid: "nav-link-simulation" },
  { label: "Pricing", href: "#pricing", testid: "nav-link-pricing" },
  { label: "Trust", href: "#trust", testid: "nav-link-compliance" },
];

const STATS = [
  { value: "$4.8M+", label: "Revenue captured by agent fleets", color: "text-emerald-400" },
  { value: "100%", label: "Deterministic hard spend stops", color: "text-cyan-400" },
  { value: "0", label: "Unapproved runaway outflows", color: "text-amber-400" },
  { value: "<12ms", label: "Anomaly freeze latency", color: "text-red-400" },
];

const ACCENT = {
  emerald: { chip: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10", line: "#10B981" },
  cyan: { chip: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10", line: "#06B6D4" },
  amber: { chip: "text-amber-400 border-amber-500/30 bg-amber-500/10", line: "#F59E0B" },
  crimson: { chip: "text-red-400 border-red-500/30 bg-red-500/10", line: "#EF4444" },
};

const BENTO = [
  { span: "lg:col-span-8", title: "Isolated Wallet-Backed Execution", tag: "Policy Engine", accent: "emerald", icon: Wallet, copy: "Each agent runs inside its own deterministic budget envelope with strict daily, per-transaction, and monthly hard stops. When balance or policy hits zero, execution halts instantly — no exceptions, no overrides in the wild." },
  { span: "lg:col-span-4", title: "Anomaly Auto-Freeze", tag: "Kill Switch", accent: "crimson", icon: Snowflake, copy: "Spend spikes, unauthorized tool calls, and retry loops freeze the agent in milliseconds and page admins." },
  { span: "lg:col-span-4", title: "Policy-Inherited Spawning", tag: "Sub-Agent Trees", accent: "cyan", icon: GitFork, copy: "Parents spawn scoped children with subdivided budgets, recursion depth limits, and inherited action whitelists." },
  { span: "lg:col-span-4", title: "Human-in-the-Loop Approvals", tag: "Governance", accent: "amber", icon: ShieldCheck, copy: "Any action over your threshold routes to an admin approval queue. Nothing settles without a human sign-off." },
  { span: "lg:col-span-4", title: "Self-Deploy Sites & APIs", tag: "Dev Infra", accent: "cyan", icon: Zap, copy: "Agents generate code, run checks, and trigger verified Vercel production deploys — funded by earned cashflow." },
];

const STEPS = [
  { num: "01", title: "Define Strict Guardrails", desc: "Set daily spend caps, approved tools, model choice (Claude Sonnet 4.6), and human approval thresholds in the console.", badge: "Zero Runaway Risk" },
  { num: "02", title: "Agents Earn Real Revenue", desc: "Your fleet qualifies prospects, chats with buyers, issues Stripe checkouts, and books earned cash to the ledger.", badge: "Earned Funds Only" },
  { num: "03", title: "Policy-Constrained Reinvestment", desc: "Agents spend their earned treasury on API tokens, child agents, or servers — strictly bounded by your rules.", badge: "Self-Sustaining" },
  { num: "04", title: "Instant Auditability & Panic Stops", desc: "Every spend, spawn, and checkout is logged to a tamper-evident audit trail with one-click global freeze.", badge: "Forensic Integrity" },
];

const TRUST = [
  { title: "Not a Stored-Value Wallet", body: "Kleen-G holds no pre-funded consumer deposits or token balances. Agent operations are funded strictly by verified business revenue — real product & order checkouts booked to an authoritative ledger.", icon: Lock },
  { title: "Multi-Tenant RBAC", body: "Granular operator privileges, scoped access, workspace isolation, and admin-only sign-off for high-stakes actions.", icon: Users },
  { title: "Claude Sonnet 4.6 Reasoning", body: "Built on frontier models for rock-solid tool usage, disciplined JSON schemas, and dependable autonomous decisions.", icon: Cpu },
  { title: "Immutable Forensic Audit Log", body: "Every state transition, spend, and child node creation is stamped with tamper-evident, hash-chained metadata.", icon: ScrollText },
];

const COMPLIANCE_BADGES = [
  { icon: Lock, label: "Non-custodial", sub: "No stored-value wallet — treasury = earned revenue only" },
  { icon: Wallet, label: "Isolated execution", sub: "Per-agent sandboxes with hard spend caps" },
  { icon: ShieldCheck, label: "Human-in-the-loop", sub: "Approvals required above your threshold" },
  { icon: ScrollText, label: "Hash-chained audit", sub: "Tamper-evident log of every action" },
  { icon: Snowflake, label: "Anomaly auto-freeze", sub: "One-click global kill switch" },
];

function ReferralBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) { try { localStorage.setItem("kleeng_ref", ref); } catch (e) {} setShow(true); }
  }, []);
  if (!show) return null;
  return (
    <div className="sticky top-0 z-50 bg-emerald-500 text-emerald-950 text-center py-2 px-4 text-sm font-bold" data-testid="referral-banner">
      🎉 You've got <span className="underline">$50 off</span> any plan — a friend sent you. It's applied automatically at checkout.
    </div>
  );
}

export default function Landing() {
  return (

    <div className="min-h-screen bg-[#080B11] text-slate-100 overflow-x-hidden">
      <ReferralBanner />
      <Navbar />
      <Hero />
      <StatsBand />
      <ProductTour />
      <Architecture />
      <HowItWorks />
      <Simulation />
      <Trust />
      <Pricing />
      <FinalCTA />
      <Footer />
    </div>
  );
}

function Navbar() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#080B11]/85 border-b border-slate-800/80">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="brand-logo">
          <img src="/kleeng-logo.png" alt="Kleen-G" className="w-16 h-16 rounded-md" />
          <div className="leading-none">
            <span className="font-display font-black tracking-tight text-3xl">Kleen-G</span>
            <span className="hidden sm:inline ml-2 font-mono text-[9px] uppercase tracking-[0.2em] text-cyan-400/80">v2.6 · Autonomy Engine</span>
          </div>
        </a>
        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} data-testid={l.testid} className="font-mono text-[11px] uppercase tracking-wider text-slate-400 hover:text-cyan-300 transition-colors">
              {l.label}
            </a>
          ))}
        </nav>
        <Link to="/login" data-testid="nav-login-btn" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 transition-colors">
          Launch Console <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative pt-20">
      <div className="absolute inset-0 grid-bg opacity-35" />
      <div className="absolute top-24 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[120px] pointer-events-none" />
      <div className="absolute top-40 right-0 w-[400px] h-[400px] rounded-full bg-emerald-500/8 blur-[120px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono text-[10px] uppercase tracking-[0.2em] mb-6">
            <Lock className="w-3 h-3" /> Legally Sandboxed · Policy-Constrained Autonomy
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] uppercase">
            Deploy Self-Funded
            <span className="block">Autonomous AI Agents.</span>
            <span className="block text-cyan-400 mt-3 text-2xl sm:text-3xl lg:text-4xl normal-case font-extrabold tracking-tight">Real earned revenue. Cryptographically-enforced policy. Zero unbounded liability.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
            Deploy agents that qualify leads, close orders, collect verified customer revenue, spawn scoped child tasks, and self-deploy live web apps — all inside immutable, policy-enforced spend caps with an instant panic freeze.
          </p>
          <div className="mt-6 inline-flex items-start gap-2.5 px-4 py-3 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.06] max-w-2xl" data-testid="hero-compliance-callout">
            <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
            <p className="text-[13px] text-slate-300 leading-relaxed">
              Treasury funded <span className="text-emerald-400 font-medium">solely by verified customer orders & Stripe checkouts</span> — not a stored-value or preloaded card wallet.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login" data-testid="hero-cta-primary" className="flex items-center gap-2 px-6 py-3.5 rounded-lg bg-cyan-500 text-[#080B11] font-bold hover:bg-cyan-400 transition-colors shadow-[0_0_30px_rgba(6,182,212,0.25)]">
              Deploy First Agent — Start Free <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#architecture" data-testid="hero-cta-secondary" className="flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-700 text-slate-200 font-semibold hover:border-cyan-500/50 hover:text-cyan-300 transition-colors">
              <Cpu className="w-4 h-4" /> Explore Control Plane
            </a>
          </div>
          <a href="#simulation" data-testid="hero-sandbox-link" className="mt-4 inline-flex items-center gap-2 font-mono text-xs text-cyan-400/90 hover:text-cyan-300 transition-colors">
            <Activity className="w-3.5 h-3.5" /> Try the live sandbox — no signup, watch an agent hit its cap & freeze <ArrowRight className="w-3 h-3" />
          </a>
          <div className="mt-9 flex flex-wrap gap-x-6 gap-y-3 font-mono text-xs text-slate-500">
            <span className="flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-emerald-400" /> Claude Sonnet 4.6</span>
            <span className="flex items-center gap-2"><Wallet className="w-3.5 h-3.5 text-cyan-400" /> Earned-revenue treasury</span>
            <span className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-amber-400" /> Multi-tenant RBAC</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.55, delay: 0.15 }}>
          <FleetTelemetry />
        </motion.div>
      </div>
    </section>
  );
}

function FleetTelemetry() {
  const nodes = [
    { name: "LeadHunter-01", role: "seller", state: "active", val: "+$420 MRR", icon: "text-emerald-400", txt: "text-emerald-400" },
    { name: "CheckoutWorker-04", role: "seller", state: "active", val: "+$1,250 order", icon: "text-emerald-400", txt: "text-emerald-400" },
    { name: "FrontendDeployer-02", role: "builder", state: "deploying", val: "vercel · live", icon: "text-cyan-400", txt: "text-cyan-400" },
  ];
  return (
    <div className="hud-card accent-top rounded-xl p-5" style={{ "--accent-line": "#06B6D4" }} data-testid="hero-telemetry">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">Fleet Telemetry</span>
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE</span>
      </div>
      <div className="space-y-2.5">
        {nodes.map((n) => (
          <div key={n.name} className="flex items-center justify-between rounded-lg border border-slate-800 bg-[#080B11] px-3 py-2.5">
            <div className="flex items-center gap-2.5">
              <Bot className={`w-4 h-4 ${n.icon}`} />
              <div>
                <div className="text-[13px] font-medium text-slate-200">{n.name}</div>
                <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{n.role} · {n.state}</div>
              </div>
            </div>
            <span className={`font-mono text-[11px] tabular-nums ${n.txt}`}>{n.val}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-lg border border-slate-800 bg-[#080B11] p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Policy Budget Meter</span>
          <span className="font-mono text-[11px] tabular-nums text-amber-400">$84.20 / $500</span>
        </div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500" style={{ width: "17%" }} />
        </div>
        <div className="mt-2 flex items-center gap-1.5 font-mono text-[9px] text-slate-500">
          <Lock className="w-3 h-3 text-cyan-400" /> Hard stop enforced at cap · human approval above threshold
        </div>
      </div>
    </div>
  );
}

function StatsBand() {
  return (
    <section className="border-y border-slate-800/80 bg-[#0A0E16]">
      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {STATS.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }} className="text-center lg:text-left">
            <div className={`font-display text-3xl lg:text-4xl font-black tabular-nums ${s.color}`}>{s.value}</div>
            <div className="mt-1 text-xs text-slate-500 leading-snug">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const TOUR_SCENES = [
  {
    key: "agent",
    step: "Step 1",
    title: "Create a Builder agent",
    caption: "Spin up an agent, set its wallet cap & approval threshold. Guardrails first — always.",
    url: "console/agents",
  },
  {
    key: "deploy",
    step: "Step 2",
    title: "Launch a deployment",
    caption: "Give it a product name and a one-line brief. Deploy cost runs through the spend engine.",
    url: "console/deployments",
  },
  {
    key: "build",
    step: "Step 3",
    title: "AI builds & ships to Vercel",
    caption: "The agent writes the site, pushes to Vercel, and provisions SSL — all non-blocking.",
    url: "console/deployments",
  },
  {
    key: "live",
    step: "Step 4",
    title: "Live at your branded domain",
    caption: "Your site is instantly online at {agent}.kleen-g.com with a valid SSL certificate.",
    url: "aurora-sites.kleen-g.com",
  },
];

function ProductTour() {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const DURATION = 4200;

  useEffect(() => {
    if (!playing) return;
    const start = Date.now();
    setProgress(0);
    const t = setInterval(() => {
      const p = Math.min(100, ((Date.now() - start) / DURATION) * 100);
      setProgress(p);
      if (p >= 100) {
        clearInterval(t);
        setActive((a) => (a + 1) % TOUR_SCENES.length);
      }
    }, 40);
    return () => clearInterval(t);
  }, [playing, active]);

  const go = (i) => { setActive(i); setProgress(0); setPlaying(true); };
  const scene = TOUR_SCENES[active];

  return (
    <section id="tour" className="border-y border-slate-800/80 bg-[#0A0E16] scroll-mt-16" data-testid="product-tour">
      <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] font-semibold tracking-[0.25em] uppercase text-cyan-400">Watch How It Works</div>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-3xl">
              Deploy a self-funded agent site in about 60 seconds.
            </h2>
          </div>
          <button data-testid="tour-play-toggle" onClick={() => setPlaying((p) => !p)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 font-mono text-[11px] uppercase tracking-wider hover:bg-cyan-500/20 transition-colors">
            {playing ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Play</>}
          </button>
        </div>

        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-5">
          {/* Player */}
          <div className="hud-card rounded-xl overflow-hidden" data-testid="tour-player">
            {/* browser chrome */}
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-800 bg-[#080B11]">
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
              </div>
              <div className="flex-1 flex items-center gap-2 rounded-md bg-[#0E131F] border border-slate-800 px-3 py-1.5 font-mono text-[11px] text-slate-400">
                {scene.key === "live" ? <Lock className="w-3 h-3 text-emerald-400" /> : <Globe className="w-3 h-3 text-slate-500" />}
                <span className="truncate">{scene.key === "live" ? "https://" : "app.kleen-g.com/"}{scene.url}</span>
              </div>
            </div>
            {/* scene */}
            <div className="relative h-[300px] sm:h-[340px] grid-bg">
              <motion.div key={scene.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="absolute inset-0 p-6">
                <TourScene which={scene.key} />
              </motion.div>
            </div>
            {/* progress rail */}
            <div className="flex gap-1 px-4 py-3 bg-[#080B11] border-t border-slate-800">
              {TOUR_SCENES.map((s, i) => (
                <button key={s.key} data-testid={`tour-dot-${i}`} onClick={() => go(i)} className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden" aria-label={s.title}>
                  <div className="h-full rounded-full bg-cyan-500 transition-[width] duration-75" style={{ width: i < active ? "100%" : i === active ? `${progress}%` : "0%" }} />
                </button>
              ))}
            </div>
          </div>

          {/* Step list */}
          <div className="space-y-2.5">
            {TOUR_SCENES.map((s, i) => (
              <button key={s.key} data-testid={`tour-step-${i}`} onClick={() => go(i)}
                className={`w-full text-left rounded-xl border p-4 transition-all duration-300 ${i === active ? "border-cyan-500/50 bg-cyan-500/[0.07]" : "border-slate-800 bg-[#080B11] hover:border-slate-700"}`}>
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 rounded ${i === active ? "bg-cyan-500 text-[#080B11]" : "bg-slate-800 text-slate-400"}`}>{s.step}</span>
                  <span className={`font-display font-bold text-sm ${i === active ? "text-slate-100" : "text-slate-300"}`}>{s.title}</span>
                </div>
                {i === active && <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">{s.caption}</p>}
              </button>
            ))}
            <Link to="/login" data-testid="tour-cta" className="mt-1 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-cyan-500 text-[#080B11] font-bold hover:bg-cyan-400 transition-colors">
              Try It Yourself <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function TourScene({ which }) {
  if (which === "agent") {
    return (
      <div className="max-w-sm">
        <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-3">New Agent</div>
        <MockField label="Name" value="Aurora Sites" />
        <MockField label="Role" value="Builder" pill />
        <MockField label="Wallet cap / day" value="$250.00" />
        <MockField label="Approval threshold" value="$100.00" />
        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm"><Bot className="w-4 h-4" /> Activate Agent</div>
      </div>
    );
  }
  if (which === "deploy") {
    return (
      <div className="max-w-sm">
        <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-3">New Deployment</div>
        <MockField label="Builder agent" value="Aurora Sites" pill />
        <MockField label="Product / site" value="Lunar Coffee" />
        <MockField label="Brief" value="single-origin coffee subscription" />
        <MockField label="Deploy cost" value="$25.00" />
        <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm"><Rocket className="w-4 h-4" /> Generate & Deploy</div>
      </div>
    );
  }
  if (which === "build") {
    const logs = [
      ["Deployment initiated by Aurora Sites", "info"],
      ["Site generated by AI · pushing to Vercel", "info"],
      ["Provisioning SSL · assigning aurora-sites.kleen-g.com", "info"],
      ["Deployment READY · live with SSL", "ok"],
    ];
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="font-display font-bold text-sm">Lunar Coffee</span>
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-400"><Activity className="w-3 h-3 animate-pulse" /> Deploying</span>
        </div>
        <div className="rounded-md border border-slate-800 bg-[#050810] p-3 font-mono text-[11px] space-y-1.5">
          {logs.map(([m, lvl], i) => (
            <motion.div key={m} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.6 }}
              className={lvl === "ok" ? "text-emerald-400" : "text-slate-400"}>
              <span className="text-slate-600 mr-2">›</span>{m}
            </motion.div>
          ))}
        </div>
      </div>
    );
  }
  // live
  return (
    <div className="h-full flex flex-col items-center justify-center text-center">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-mono text-[11px]">
        <ShieldCheck className="w-3.5 h-3.5" /> SSL secured · live
      </div>
      <div className="mt-4 font-display text-2xl font-black tracking-tight">aurora-sites<span className="text-cyan-400">.kleen-g.com</span></div>
      <p className="mt-2 text-sm text-slate-400 max-w-xs">Your agent's site is online at its own branded subdomain — funded by the revenue it earns.</p>
      <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 font-mono text-xs text-emerald-400">
        <Globe className="w-3.5 h-3.5" /> HTTP 200 · certificate valid
      </div>
    </div>
  );
}

function MockField({ label, value, pill }) {
  return (
    <div className="mb-2.5">
      <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500 mb-1">{label}</div>
      {pill
        ? <span className="inline-block font-mono text-[11px] px-2 py-1 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300">{value}</span>
        : <div className="rounded-md bg-[#0E131F] border border-slate-800 px-3 py-1.5 text-[13px] text-slate-200">{value}</div>}
    </div>
  );
}

function Architecture() {
  return (
    <section id="architecture" className="max-w-7xl mx-auto px-6 py-20 lg:py-28 scroll-mt-16">
      <div className="mb-10">
        <div className="font-mono text-[10px] font-semibold tracking-[0.25em] uppercase text-cyan-400">The Governed Autonomy Matrix</div>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-3xl">
          Agents in production — without giving AI an unmonitored credit card.
        </h2>
      </div>
      <div id="guardrails" className="grid grid-cols-1 lg:grid-cols-12 gap-4 scroll-mt-16">
        {BENTO.map((c, i) => {
          const a = ACCENT[c.accent];
          return (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
              className={`hud-card accent-top rounded-xl p-6 hover:border-cyan-500/50 hover:shadow-[0_0_25px_rgba(6,182,212,0.12)] transition-all duration-300 ${c.span}`}
              style={{ "--accent-line": a.line }}
              data-testid={`bento-card-${i}`}
            >
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${a.chip}`}><c.icon className="w-5 h-5" /></div>
                <span className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-1 rounded border ${a.chip}`}>{c.tag}</span>
              </div>
              <h3 className="mt-4 font-display font-bold text-lg">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.copy}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how-it-works" className="border-y border-slate-800/80 bg-[#0A0E16] scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <div className="mb-12">
          <div className="font-mono text-[10px] font-semibold tracking-[0.25em] uppercase text-emerald-400">How Self-Funded Agentic Commerce Works</div>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-3xl">
            From inbound qualification to closed revenue and autonomous reinvestment.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => (
            <motion.div key={s.num} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }} className="hud-card rounded-xl p-6 relative">
              <div className="font-display text-4xl font-black text-slate-800">{s.num}</div>
              <h3 className="mt-3 font-display font-bold text-base">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider px-2 py-1 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <CheckCircle2 className="w-3 h-3" /> {s.badge}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Simulation() {
  const [balance, setBalance] = useState(84.2);
  const cap = 500;
  const [frozen, setFrozen] = useState(false);
  const [pending, setPending] = useState(false);
  const [log, setLog] = useState([{ t: "Agent online · policy loaded", c: "cyan" }]);

  const push = (t, c) => setLog((l) => [{ t, c }, ...l].slice(0, 6));

  const earn = () => {
    if (frozen) return;
    setBalance((b) => +(b + 120).toFixed(2));
    push("Revenue captured: +$120.00 deposited to treasury", "emerald");
    toast.success("Revenue captured: +$120.00 deposited");
  };
  const bigSpend = () => {
    if (frozen) return;
    setPending(true);
    push("$350.00 API task exceeds threshold → routed to human approval", "amber");
    toast("Human approval required for $350.00 spend", { icon: "🛡️" });
  };
  const approve = () => {
    setBalance((b) => +(b - 350).toFixed(2));
    setPending(false);
    push("Admin approved $350.00 · executed under policy", "cyan");
    toast.success("Spend approved & executed");
  };
  const spike = () => {
    setFrozen(true);
    setPending(false);
    push("ANOMALY: spend velocity spike detected → agent auto-frozen", "crimson");
    toast.error("Anomaly detected: agent frozen");
  };
  const reset = () => {
    setFrozen(false); setPending(false); setBalance(84.2);
    setLog([{ t: "Agent online · policy loaded", c: "cyan" }]);
    toast.success("Simulation reset · agent reactivated");
  };

  const pct = Math.min(100, Math.max(0, (balance / cap) * 100));

  return (
    <section id="simulation" className="max-w-7xl mx-auto px-6 py-20 lg:py-28 scroll-mt-16">
      <div className="mb-10">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="font-mono text-[10px] font-semibold tracking-[0.25em] uppercase text-amber-400">Interactive Operator Simulation</div>
          <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live · no signup
          </span>
        </div>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-3xl">
          Test lead conversion, budget depletion, and policy triggers — live.
        </h2>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
        <div className={`hud-card accent-top rounded-xl p-6 transition-all duration-300 ${frozen ? "border-red-500/40" : ""}`} style={{ "--accent-line": frozen ? "#EF4444" : "#06B6D4" }} data-testid="sim-panel">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Agent · SaaS Closer</span>
            <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${frozen ? "border-red-500/40 bg-red-500/10 text-red-400" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"}`} data-testid="sim-status">
              {frozen ? <><Snowflake className="w-3 h-3" /> Frozen</> : <><Activity className="w-3 h-3" /> Active</>}
            </span>
          </div>

          <div className="mt-5 rounded-lg border border-slate-800 bg-[#080B11] p-4">
            <div className="flex items-end justify-between mb-2">
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Treasury Balance</span>
              <span className="font-display text-2xl font-black tabular-nums text-emerald-400" data-testid="sim-balance">${balance.toFixed(2)}</span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${frozen ? "bg-red-500" : "bg-gradient-to-r from-emerald-500 to-cyan-500"}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-1.5 font-mono text-[9px] text-slate-500">Hard cap ${cap} · earned-revenue funded</div>
          </div>

          {pending && (
            <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-center justify-between" data-testid="sim-approval">
              <span className="text-[13px] text-amber-300 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> $350.00 awaiting approval</span>
              <button onClick={approve} data-testid="sim-approve-btn" className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/30 transition-colors">Approve</button>
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 gap-2.5">
            <SimBtn onClick={earn} disabled={frozen} testid="sim-earn-btn" icon={TrendingUp} color="emerald">Convert Lead (+$120)</SimBtn>
            <SimBtn onClick={bigSpend} disabled={frozen || pending} testid="sim-spend-btn" icon={DollarSign} color="amber">High Spend ($350)</SimBtn>
            <SimBtn onClick={spike} disabled={frozen} testid="sim-spike-btn" icon={AlertTriangle} color="crimson">Trigger Anomaly</SimBtn>
            <SimBtn onClick={reset} testid="sim-reset-btn" icon={Zap} color="cyan">Reset Simulation</SimBtn>
          </div>
        </div>

        <div className="hud-card rounded-xl p-6" data-testid="sim-log">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Event Stream</span>
          <div className="mt-4 space-y-2">
            {log.map((e, i) => {
              const c = ACCENT[e.c] || ACCENT.cyan;
              return (
                <motion.div key={`${e.t}-${i}`} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} className={`rounded-md border px-3 py-2 text-[12px] leading-snug ${c.chip}`}>
                  <span className="font-mono text-[9px] opacity-60 mr-2">›</span>{e.t}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function SimBtn({ onClick, disabled, testid, icon: Icon, color, children }) {
  const a = ACCENT[color];
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg border font-mono text-[10px] uppercase tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${a.chip} hover:brightness-125`}>
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}

function Trust() {
  return (
    <section id="trust" className="border-y border-slate-800/80 bg-[#0A0E16] scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <div className="mb-12">
          <div className="font-mono text-[10px] font-semibold tracking-[0.25em] uppercase text-cyan-400">Legal & Financial Sandboxing</div>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-3xl">
            Enterprise-grade guardrails, not a black-box wallet.
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="compliance-badges">
          {COMPLIANCE_BADGES.map((b, i) => (
            <motion.div key={b.label} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: i * 0.05 }}
              className="hud-card rounded-xl p-4 text-center flex flex-col items-center">
              <div className="w-9 h-9 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 flex items-center justify-center"><b.icon className="w-5 h-5" /></div>
              <div className="mt-2.5 font-display font-bold text-[13px] leading-tight">{b.label}</div>
              <div className="mt-1 text-[11px] text-slate-500 leading-snug">{b.sub}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {TRUST.map((t, i) => (
            <motion.div key={t.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }} className="hud-card rounded-xl p-6 flex gap-4">
              <div className="w-10 h-10 rounded-md border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 flex items-center justify-center shrink-0"><t.icon className="w-5 h-5" /></div>
              <div>
                <h3 className="font-display font-bold text-base">{t.title}</h3>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{t.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PRICING = [
  {
    name: "Basic", price: "$9.99", period: "/mo", accent: "#64748B", highlight: false,
    tagline: "For solo operators testing the waters.",
    features: ["1–3 autonomous agents", "Wallet-backed hard spend caps", "Human-in-the-loop approvals", "Branded {agent}.kleen-g.com deploys", "Community support"],
    cta: "Get Started",
  },
  {
    name: "Pro", price: "$99.99", period: "/mo", accent: "#06B6D4", highlight: true,
    tagline: "For teams running a real agent fleet.",
    features: ["Up to 15 agents", "Autopilot + child-agent spawning", "Team seats (RBAC) & realtime telemetry", "Weekly revenue digest emails", "Priority email support"],
    cta: "Start with Pro",
  },
  {
    name: "Org", price: "$999", period: "/mo", accent: "#10B981", highlight: false,
    tagline: "For scaled, multi-team operations.",
    features: ["Up to 100 agents", "Everything in Pro", "Advanced audit & policy controls", "Dedicated onboarding", "Priority support & SLAs"],
    cta: "Choose Org",
  },
];

function Pricing() {
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 py-20 lg:py-28 scroll-mt-16" data-testid="pricing-section">
      <div className="mb-12 text-center">
        <div className="font-mono text-[10px] font-semibold tracking-[0.25em] uppercase text-cyan-400">Simple, Scaling Pricing</div>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
          Pay for the size of your fleet — not surprises.
        </h2>
        <p className="mt-3 text-sm text-slate-400 max-w-xl mx-auto">Every plan runs on earned-revenue treasury with the same hard-stop guardrails. Upgrade or downgrade anytime.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-5 items-stretch">
        {PRICING.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
            className={`hud-card accent-top rounded-2xl p-7 flex flex-col relative ${p.highlight ? "ring-1 ring-cyan-500/40 shadow-[0_0_40px_rgba(6,182,212,0.15)] md:-translate-y-3" : ""}`}
            style={{ "--accent-line": p.accent }}
            data-testid={`pricing-tier-${p.name.toLowerCase()}`}
          >
            <div className="h-6 mb-1 flex justify-center">
              {p.highlight && (
                <span className="font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-1 rounded-full bg-cyan-500 text-[#080B11] font-bold">Most Popular</span>
              )}
            </div>
            <div className="font-display font-black text-lg">{p.name}</div>
            <p className="mt-1 text-[13px] text-slate-500 min-h-[36px]">{p.tagline}</p>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-display text-4xl font-black tabular-nums">{p.price}</span>
              <span className="font-mono text-xs text-slate-500 mb-1.5">{p.period}</span>
            </div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[13px] text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Link
              to={`/login?plan=${p.name.toLowerCase()}`}
              data-testid={`pricing-cta-${p.name.toLowerCase()}`}
              className={`mt-7 w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold transition-colors ${p.highlight ? "bg-cyan-500 text-[#080B11] hover:bg-cyan-400" : "border border-slate-700 text-slate-100 hover:border-cyan-500/50 hover:text-cyan-300"}`}
            >
              {p.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        ))}
      </div>
      <p className="mt-6 text-center font-mono text-[11px] text-slate-500">All plans start with a free trial · no card required to explore the sandbox</p>
    </section>
  );
}

function FinalCTA() {  return (
    <section className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
      <div className="hud-card accent-top rounded-2xl p-10 lg:p-14 text-center relative overflow-hidden" style={{ "--accent-line": "#06B6D4" }}>
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-cyan-500/10 blur-[120px] pointer-events-none" />
        <div className="relative">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight uppercase">Launch your autonomous workforce.</h2>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">Join elite operators deploying self-funded, policy-constrained AI agents — with a panic freeze always one click away.</p>
          <Link to="/login" data-testid="footer-cta-btn" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-cyan-500 text-[#080B11] font-bold hover:bg-cyan-400 transition-colors shadow-[0_0_30px_rgba(6,182,212,0.25)]">
            Enter Operator Console <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="mt-5 font-mono text-[10px] uppercase tracking-wider text-slate-500">Free tier available · Instant setup · No card needed to explore the sandbox</div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-slate-800/80 py-10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <img src="/kleeng-logo.png" alt="Kleen-G" className="w-8 h-8 rounded-md" />
            <div>
              <div className="font-display font-black text-sm tracking-tight leading-none">Kleen-G</div>
              <div className="font-mono text-[9px] text-slate-500 tracking-[0.15em] uppercase mt-0.5">Deploy Self-Funded Autonomous Agents</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] text-slate-500">
            <a href="#architecture" className="hover:text-cyan-300 transition-colors">Architecture</a>
            <a href="#guardrails" className="hover:text-cyan-300 transition-colors">Safety Plane</a>
            <a href="#simulation" className="hover:text-cyan-300 transition-colors">Simulation</a>
            <Link to="/login" className="hover:text-cyan-300 transition-colors">Console</Link>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-slate-800/60 text-[11px] text-slate-600 font-mono leading-relaxed">
          © 2026 Kleen-G · Autonomous Operations Plane. All agent actions are policy-constrained, legally sandboxed, and admin-supervised. Treasury balances reflect earned revenue streams under human-controlled guardrails.
        </div>
      </div>
    </footer>
  );
}
